
import { Component, Prop, Emit } from 'vue-property-decorator'
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
import { formatDate } from '@/utils/date'
import {
    mapStatusApp,
    ENQUEUED,
    REQUESTED,
    BUILDING,
    WAITING_FOR_REVIEW,
    WAITING_TO_SEND_TO_REVIEW,
    PREPARE_FOR_SUBMISSION,
    SENDING_TO_REVIEW,
    IN_REVIEW,
    READY_FOR_SALE,
    ERROR,
    UNKNOWN,
    REJECTED,
    WAITING_AUTH,
} from '@/utils/statusApp'
import { headAccess } from '@/utils/userType'
import { AppButtonOutline } from '@/components'
import { ChildComponentMixin } from '@/mixins'
import { appInfoEntity } from '@/models/models'

type AppPlatformType = 'android' | 'ios'

@Component({
    components: {
        AppButtonOutline,
        FontAwesomeIcon,
    },
})
export default class CommonAppItemPlatform extends ChildComponentMixin {
    @Prop() private value?: AppInfoEntity
    @Prop() private account?: AppAccountEntity
    @Prop() private platformType!: AppPlatformType
    @Prop() private appId!: string

    @Emit('updateSession')
    public onClickUpdateSession(e: Event) {
        return e
    }

    get platform(): AppInfoEntity {
        return this.value != null ? this.value! : appInfoEntity
    }

    get deletableStatus(): string[] {
        return [REQUESTED, ERROR, UNKNOWN, REJECTED, WAITING_AUTH]
    }

    get greenStatus(): string[] {
        return [READY_FOR_SALE]
    }

    get yellowStatus(): string[] {
        return [
            REQUESTED,
            ENQUEUED,
            BUILDING,
            PREPARE_FOR_SUBMISSION,
            WAITING_TO_SEND_TO_REVIEW,
            WAITING_FOR_REVIEW,
            IN_REVIEW,
            SENDING_TO_REVIEW,
        ]
    }

    get devStatus(): string[] {
        return [UNKNOWN]
    }

    get canUpdateApps(): boolean {
        return this.$store.getters.canUpdateApps
    }

    get canUpdateSession(): boolean {
        return this.canUpdateApps &&
            this.platformType === 'ios' &&
            this.account?.type === 'individual' &&
            (this.account?.sessionMissingOrExpired ?? false)
    }

    public showPriority(update: AppUpdateEntity): boolean {
        const hasAccess = headAccess.includes(this.$store.getters.authenticationType)
        return update.status === REQUESTED && hasAccess
    }

    public getStatusName(status: string): string {
        const response = mapStatusApp.get(status)
        if (response === undefined) {
            return status
        }
        return response
    }

    public getFormattedSubtitle(update: AppUpdateEntity): string {
        let subtitle = formatDate(update.lastModified)
        if (update.cxRequester) {
            subtitle = `${subtitle} • ${update.cxRequester.split('@')[0]}`
        }
        return subtitle
    }

    private goToRequestUpdate() {
        this.$router.push(`/app/update/${this.appId}/${this.platformType}`)
    }

    private async onDeleteUpdate(e: Event, update: AppUpdateEntity) {
        e.stopPropagation()
        const deleted = await this.$store.dispatch(
            'detail/deleteUpdate',
            update,
        )

        if (!deleted) {
            return
        }

        const updates = this.platform.updates.filter((u) => u.id !== update.id)
        this.update(this.platform, 'updates', updates)
    }

    private async onChangePriority(e: Event, update: AppUpdateEntity) {
        e.stopPropagation()
        const index = this.platform.updates.findIndex((u) => u.id === update.id)
        const updates = this.platform.updates
        const oldPriority = update.priority

        if (typeof index !== 'undefined') {
            updates[index].priority = update.priority < 3 ? 3 : 0
            this.update(this.platform, 'updates', updates)
        } else {
            return
        }

        const request = await this.$store.dispatch(
            'detail/changePriority',
            update,
        )

        if (!request) {
            updates[index].priority = oldPriority
            this.update(this.platform, 'updates', updates)
        }
    }
}
